import {
  Component,
  ContentChild,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  Observable,
  of,
  take,
  tap,
} from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import {
  ACTIONS_TEXT,
  IMAGE_SRC,
  ITEMS_PAGE_LABEL,
  STATUS_OPTIONS,
  STOP_TYPE_OPTIONS,
} from 'src/app/components/shared/shared-constants';
import { TemplateMarkerDirective } from 'src/app/core/directives/template-marker/template-marker.directive';
import { SpecialCharacters } from 'src/app/core/enums';
import { UserAdmin } from 'src/app/core/models/admin';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import {
  AddOnDemandPickupRequest,
  AddOnDemandPickupResponse,
  EditOnDemandPickupRequest,
  OnDemandPickupResponse,
} from 'src/app/core/models/operations/operations-on-demand-pickup.model';
import {
  AssignReassignRequest,
  OnDemandPickupBody,
  OperationsPickup,
  PickupColumn,
  SaveScheduledPickupRequest,
  SaveScheduledPickupResponse,
} from 'src/app/core/models/operations/operations-pickup.model';
import { ScheduledPickupResponse } from 'src/app/core/models/operations/operations-scheduled-pickup.model';
import { PickupAssignmentsService } from 'src/app/core/services/operations/pickup-assignments/pickup-assignments.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { OnDemandPickupComponent } from './on-demand-pickup/on-demand-pickup.component';
import {
  ColumnIds,
  ColumnText,
  EXCEL_FILE_NAME,
  FLOOR,
  PACKAGE_TYPES,
  ROUTE_STATUSES,
  STATUSES,
  STOP_TYPES,
  SUITE,
} from './pickup.constants';
import { ScheduledPickupComponent } from './scheduled-pickup/scheduled-pickup.component';

@Component({
  selector: 'app-pickup',
  templateUrl: './pickup.component.html',
  styleUrls: ['./pickup.component.scss'],
  standalone: false,
})
export class PickupComponent implements OnInit, OnChanges {
  @Input()
  pickupData$!: Observable<OperationsPickup[]>;

  @Input()
  resetClick!: BehaviorSubject<boolean>;

  @Input()
  exportExcelEvent!: BehaviorSubject<boolean>;

  @Input()
  summaryRoutes: string[] = [];

  @Input()
  addPickupEvent!: EventEmitter<{
    response: SaveScheduledPickupResponse | AddOnDemandPickupResponse;
    data: SaveScheduledPickupRequest | AddOnDemandPickupRequest;
  }>;

  emitEnableClearFilterEvent = output<boolean>();
  refetchData = output<boolean>();

  readonly destroy = inject(DestroyRef);
  private userService = inject(UserService);

  @ViewChild(MatSort) sort!: MatSort;

  @ContentChild(TemplateMarkerDirective) content!: TemplateMarkerDirective;

  selectedRouteId = '';
  loadedData = false;

  readonly ITEMS_PAGE_LABEL = ITEMS_PAGE_LABEL;
  readonly displayedColumns = Object.values(ColumnIds);
  readonly HEADERS = ColumnText;
  readonly SRC = IMAGE_SRC;
  readonly ACTIONS_TEXT = ACTIONS_TEXT;
  readonly SELECT_ALL_TEXT = STATUS_OPTIONS.ALL_SELECTED;
  readonly TOOLTIP_TEXT = STOP_TYPE_OPTIONS;
  readonly PACKAGES = PACKAGE_TYPES;
  readonly PICKUP_STATUS = STATUSES;
  readonly STOPS = STOP_TYPES;
  readonly STATUS = STATUS_OPTIONS;
  readonly SPECIAL_CHARS = SpecialCharacters;
  readonly IMAGES = IMAGE_SRC;

  dataSource: MatTableDataSource<OperationsPickup> =
    new MatTableDataSource<OperationsPickup>([]);

  resetSelectDropdowns = new BehaviorSubject<boolean>(false);

  routeStatusData: PickupColumn = {
    itemId: '',
    ids: [],
    checkAllIds: true,
    searchResults: ROUTE_STATUSES,
    formControl: new FormControl(''),
  };
  showRouteSortDropdown = false;

  stopTypeData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.STOP_TYPE,
  );

  routeData: TableColumn = this.commonUtils.createTableColumn(ColumnIds.ROUTE);
  IDOsuggestionData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.IDO_SUGGESTION,
  );
  statusData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.STATUS,
  );
  packageTypeData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.PACKAGE_TYPE,
  );
  shipperPrnData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.SHIPPER_PRN,
  );
  streetAddressData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.STREET_ADDRESS,
  );
  cityData: TableColumn = this.commonUtils.createTableColumn(ColumnIds.CITY);
  postalData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.POSTAL,
  );
  readyTimeData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.READY_TIME,
  );
  commitTimeData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.COMMIT_TIME,
  );
  completeTimeData: TableColumn = this.commonUtils.createTableColumn(
    ColumnIds.COMPLETE_TIME,
  );

  showStreetAddressOptions = false;
  locationCode = '';
  locationGroup = '';
  assignReAssignRouteId = '';
  assignReassignOrderNumber = '';
  originalData!: OperationsPickup[];
  isIDO$ = new BehaviorSubject<boolean>(false);
  onDemandPickupData!: OnDemandPickupResponse;
  changedRoutes: OnDemandPickupBody[] = [];
  dropdownOpen = [false, false];
  stopTypeDropdownOpen = [false, false];

  constructor(
    public commonUtils: CommonUtils,
    private readonly scheduledDialog: MatDialog,
    private readonly pickupAssignmentsService: PickupAssignmentsService,
  ) {}

  ngOnInit() {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((user): user is UserAdmin => !!user),
        tap((user) => {
          this.locationCode = user?.location;
          this.locationGroup = user?.locationGroup;
          this.dataSource.filterPredicate = this.commonUtils.createFilter();
        }),
      )
      .subscribe();

    this.resetClick
      .asObservable()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((value) => value),
      )
      .subscribe(() => {
        this.clearAllFilters();
      });

    this.exportExcelEvent
      .asObservable()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((value) => value),
        catchError(() => {
          this.commonUtils.updateBannerType(BANNER_TYPE.DOWNLOAD_UNSUCCESSFUL);
          return of(false);
        }),
      )
      .subscribe(() => {
        this.exportExcel();
      });

    this.addPickupEvent
      .pipe(
        takeUntilDestroyed(this.destroy),
        map(
          (data: {
            response: SaveScheduledPickupResponse | AddOnDemandPickupResponse;
            data: SaveScheduledPickupRequest | AddOnDemandPickupRequest;
          }) => {
            if (data?.response?.code === 200) {
              this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
              this.refetchData.emit(true);
            }
          },
        ),
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.subscribeToSummaryData(changes);
  }

  subscribeToSummaryData(changes: SimpleChanges) {
    const changedDataSource$: Observable<OperationsPickup[]> =
      changes.pickupData$?.currentValue;

    // Handle pickup data updates
    changedDataSource$
      ?.pipe(takeUntilDestroyed(this.destroy))
      .subscribe((summary) => {
        this.dataSource.data = summary.map((value) => {
          return {
            ...value,
            isRouteAssigned: !!value.route,
            addressCorrected: value.addressCorrected,
          };
        });
        this.originalData = summary;

        this.originalData.forEach((data) => {
          if (data.route) {
            data.isRouteAssigned = !!data.route;
          }
        });
      });

    // Handle summary routes updates
    if (changes.summaryRoutes?.currentValue) {
      this.summaryRoutes = Array.from(
        new Set(changes.summaryRoutes?.currentValue),
      );
    }
  }

  convertToOperationPickup(
    data: SaveScheduledPickupRequest | AddOnDemandPickupRequest,
    response: SaveScheduledPickupResponse | AddOnDemandPickupResponse,
  ): OperationsPickup {
    // OnDemand pickup
    if ((data as AddOnDemandPickupRequest).onDemand) {
      return this.convertOnDemandPickupData(data as AddOnDemandPickupRequest);

      // Scheduled Pickup
    } else if ((data as SaveScheduledPickupRequest).schedulePickup) {
      return this.convertScheduledData(
        data as SaveScheduledPickupRequest,
        response,
      );
    } else {
      return {} as OperationsPickup;
    }
  }

  convertScheduledData(
    data: SaveScheduledPickupRequest,
    response: SaveScheduledPickupResponse,
  ): OperationsPickup {
    const custInfo = data.schedulePickup.customerInfo;

    const streetPrefix = custInfo.prefix ?? '';
    const streetNumber = custInfo.streetAddress ?? '';
    const streetName = custInfo.streetName ?? '';
    const suite = custInfo.suite ? `, ${SUITE} ${custInfo.suite}` : '';
    const floor = custInfo.floor ? `, ${FLOOR} ${custInfo.floor}` : '';
    const streetSuffix = custInfo.suffix ?? '';

    const streetAddress = `${streetPrefix} ${streetNumber} ${streetName} ${streetSuffix} ${suite} ${floor}`;

    return {
      id:
        data.schedulePickup.id !== 0
          ? data.schedulePickup.id
          : (response.response?.id ?? 0),
      route: data.schedulePickup.route,
      stopType: STOP_TYPES[1].id,
      IDOsuggestion: data.schedulePickup.IDOsuggestion ?? '',
      status: data.schedulePickup.status ?? '',
      packageType: data.schedulePickup.packageType ?? '',
      shipperPrn: custInfo?.shipperNo,
      customerName: custInfo?.customerName,
      streetAddress: streetAddress,
      city: custInfo?.cityTown,
      province: custInfo?.province,
      postal: custInfo?.postalCode,
      readyTime: data.schedulePickup.readyTime,
      commitTime: data.schedulePickup.commitTime,
      completeTime: data.schedulePickup.closingTime,
      isRouteAssigned: !!data.schedulePickup.route,
      countryCode: '',
    };
  }

  convertOnDemandPickupData(data: AddOnDemandPickupRequest): OperationsPickup {
    const streetPrefix = data.streetPrefix ? `${data.streetPrefix}` : '';
    const streetNumber = data.streetAddress ? `${data.streetAddress}` : '';
    const streetName = data.streetName ? `${data.streetName} ` : '';
    const streetType = data.streetType ? `${data.streetType}` : '';
    const suite = data.room ? `, ${SUITE} ${data.room}` : '';
    const floor = data.floor ? `, ${FLOOR} ${data.floor}` : '';
    const streetSuffix = data.streetSuffix ? `${data.streetSuffix}` : '';

    const streetAddress = `${streetPrefix} ${streetNumber} ${streetName} ${streetType} ${streetSuffix} ${suite} ${floor}`;
    return {
      id: 0,
      route: data.route,
      stopType: STOP_TYPES[0].id,
      IDOsuggestion: data.IDOSuggestion ?? '',
      status: data.status ?? '',
      packageType: data.packageType ?? '',
      shipperPrn: data.prnNr,
      customerName: data.customerName,
      streetAddress: streetAddress,
      city: data.politicalDivision2,
      province: data.politicalDivision1,
      postal: data.postalCode,
      readyTime: data.readyTime,
      commitTime: data.commitTime,
      completeTime: data.closingtime,
      isRouteAssigned: !!data.route,
      countryCode: '',
    };
  }

  updateRow(newValue: OperationsPickup) {
    this.dataSource.data.forEach((dat) => {
      if (dat.id === newValue.id) {
        dat.route = newValue.route;
        dat.stopType = newValue.stopType;
        dat.IDOsuggestion = newValue.IDOsuggestion;
        dat.status = newValue.status;
        dat.packageType = newValue.packageType;
        dat.shipperPrn = newValue.shipperPrn;
        dat.customerName = newValue.customerName;
        dat.streetAddress = newValue.streetAddress;
        dat.city = newValue.city;
        dat.province = newValue.province;
        dat.postal = newValue.postal;
        dat.readyTime = newValue.readyTime;
        dat.commitTime = newValue.commitTime;
        dat.completeTime = newValue.completeTime;
        dat.addressCorrected = newValue.addressCorrected;
      }
    });
  }

  sortDataSource(direction: string) {
    this.dataSource.data.sort((a, b) => {
      let comparatorResult = 0;

      if (a.route != null && b.route != null) {
        // Check if one route value is greater than the other; if equal, comparatorResult should remain 0.
        if (a.route > b.route) {
          comparatorResult = 1;
        } else if (a.route < b.route) {
          comparatorResult = -1;
        }
      } else if (direction === 'asc') {
        // check if sort by unassigned
        if (a.route != null) {
          comparatorResult = 1;
        } else if (b.route != null) {
          comparatorResult = -1;
        }
      } else {
        // check if sort by assigned
        if (a.route != null) {
          comparatorResult = -1;
        } else if (b.route != null) {
          comparatorResult = 1;
        }
      }

      return comparatorResult;
    });
    this.dataSource._updateChangeSubscription();
  }

  exportExcel(): void {
    const filteredTable = this.dataSource.filteredData.map((data) => ({
      ROUTE: data.route,
      STATUS: data.status,
      'REQUEST/SHIPPER NO.': data.shipperPrn,
      'STOP TYPE': data.stopType,
      'READY TIME': data.readyTime,
      'COMPLETE TIME': data.completeTime,
      ADDRESS: `${data.customerName},${data.streetAddress},${data.streetAddress},${data.city},${data.province},${data.postal}`,
    }));
    this.commonUtils.exportExcel([], EXCEL_FILE_NAME, filteredTable);
  }

  search() {
    this.commonUtils.searchFilter(
      [
        this.routeData,
        this.IDOsuggestionData,
        this.shipperPrnData,
        this.streetAddressData,
        this.commitTimeData,
        this.cityData,
        this.postalData,
        this.readyTimeData,
        this.completeTimeData,
      ],
      this.dataSource,
      [this.stopTypeData, this.statusData, this.packageTypeData],
    );
    const filter = this.isFilterEnabled;
    this.emitEnableClearFilterEvent.emit(filter);
  }

  onStatusChangeForDropdowns(
    $event: string | boolean | unknown[],
    field: TableColumn,
  ) {
    field.ids[0] = $event as unknown as Items;
    this.search();
  }

  // Selecting route statuses
  routeStatusChanged() {
    const checkboxes = this.routeStatusData.searchResults.length;
    let selected = 0;
    this.routeStatusData.searchResults.forEach(
      (el: { selected: boolean; id: string }) => {
        if (el.selected) {
          selected++;
        }
      },
    );
    this.routeStatusData.checkAllIds = selected === checkboxes;
  }

  // Selecting all route statuses
  onAllRouteSelect(event: boolean) {
    this.routeStatusData.checkAllIds = event;
    this.commonUtils.selectAllForCheckedColumn(
      this.routeStatusData.searchResults,
      this.routeStatusData.checkAllIds,
    );
  }

  clearAllFilters() {
    this.commonUtils.clearAllFilters(
      this.dataSource,
      this.originalData,
      [
        this.routeData.formControl,
        this.IDOsuggestionData.formControl,
        this.shipperPrnData.formControl,
        this.streetAddressData.formControl,
        this.cityData.formControl,
        this.postalData.formControl,
        this.readyTimeData.formControl,
        this.commitTimeData.formControl,
        this.completeTimeData.formControl,
      ],
      [this.stopTypeData, this.statusData, this.packageTypeData],
    );
    this.resetSelectDropdowns.next(true);
    this.emitEnableClearFilterEvent.emit(false);
  }

  get isFilterEnabled() {
    const stopTypeItems = this.stopTypeData?.ids?.filter((item) => item);
    const stautsItems = this.statusData?.ids?.filter((item) => item);
    const packageTypeItems = this.packageTypeData?.ids?.filter((item) => item);

    return (
      stopTypeItems.length > 0 ||
      stautsItems.length > 0 ||
      packageTypeItems.length > 0 ||
      this.routeData?.formControl.value !== '' ||
      this.IDOsuggestionData?.formControl.value !== '' ||
      this.shipperPrnData?.formControl.value !== '' ||
      this.cityData?.formControl.value !== '' ||
      this.postalData?.formControl.value !== '' ||
      this.streetAddressData?.formControl.value !== '' ||
      this.readyTimeData?.formControl.value !== '' ||
      this.commitTimeData?.formControl.value !== '' ||
      this.completeTimeData?.formControl.value !== ''
    );
  }

  openScheduledPickup(pickup: OperationsPickup) {
    this.pickupAssignmentsService
      .getScheduledPickups(this.locationCode, pickup.shipperPrn, pickup.id)
      .pipe(take(1))
      .subscribe((data: ScheduledPickupResponse) => {
        if (data?.response?.schedulePickup) {
          this.scheduledDialog
            .open(ScheduledPickupComponent, {
              disableClose: true,
              minWidth: '66.75rem',
              minHeight: '37.5rem',
              data: {
                data: data,
                orderNumber: pickup.shipperPrn,
                routes: this.summaryRoutes,
                id: pickup.id,
                status: pickup.status,
                IDOsuggestion: pickup.IDOsuggestion,
                packageType: pickup.packageType,
                countryCode: pickup.countryCode,
                isIdoEnabled: pickup.idoEnabled,
              },
            })
            .afterClosed()
            .subscribe((data) => {
              if (data) {
                this.pickupAssignmentsService
                  .saveScheduledPickup(data, this.locationCode)
                  .pipe(take(1))
                  .subscribe((response) => {
                    if (response.code === 200) {
                      this.commonUtils.updateBannerType(
                        BANNER_TYPE.API_SUCCESS,
                      );

                      this.updateRow(
                        this.convertToOperationPickup(
                          data as SaveScheduledPickupRequest,
                          response,
                        ),
                      );
                    } else {
                      this.commonUtils.updateBannerType(BANNER_TYPE.API_ERROR);
                    }
                  });
              }
            });
        } else {
          this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
        }
      });
  }

  openOnDemandPickup(pickup: OperationsPickup) {
    this.pickupAssignmentsService
      .getOnDemandPickups(this.locationCode, pickup.shipperPrn)
      .pipe(take(1))
      .subscribe((data) => {
        if (data?.response?.onDemandPickup) {
          this.scheduledDialog
            .open(OnDemandPickupComponent, {
              disableClose: true,
              minWidth: '66.75rem',
              minHeight: '37.5rem',
              data: {
                data: data,
                status: pickup.status,
                IDOsuggestion: pickup.IDOsuggestion,
                packageType: pickup.packageType,
                countryCode: pickup.countryCode,
                locationGroup: this.locationGroup,
                locationCode: this.locationCode,
              },
            })
            .afterClosed()
            .subscribe(
              (data: {
                request: EditOnDemandPickupRequest;
                shipperPrn: string;
              }) => {
                if (data) {
                  this.pickupAssignmentsService
                    .editOnDemandPickup(data.request, this.locationCode)
                    .pipe(take(1))
                    .subscribe((response) => {
                      if (response.code === 200) {
                        this.commonUtils.updateBannerType(
                          BANNER_TYPE.API_SUCCESS,
                        );
                        this.convertToTableFormat(data);
                      } else {
                        this.commonUtils.updateBannerType(
                          BANNER_TYPE.API_ERROR,
                        );
                      }
                    });
                }
              },
            );
        } else {
          this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
        }
      });
  }

  convertToTableFormat(data: {
    request: EditOnDemandPickupRequest;
    shipperPrn: string;
  }) {
    const requestData = data.request.ondemandPickup;
    this.dataSource.data.forEach((row) => {
      if (row.shipperPrn === data.shipperPrn) {
        row.city = requestData.cityTown;
        row.commitTime = requestData.commitTime;
        row.readyTime = requestData.readyTime;
        row.completeTime = requestData.closingTime;
        row.customerName = requestData.custName;
        row.province = requestData.province;
        row.postal = requestData.postalCode;

        const suite = requestData.suite ? `${SUITE} ${requestData.suite},` : '';
        const floor = requestData.floor ? `${FLOOR} ${requestData.floor}` : '';
        row.streetAddress = `${requestData.prefix} ${requestData.streetAddress} ${requestData.streetName} ${requestData.streetType} ${requestData.suffix} ${suite} ${floor}`;
      }
    });
  }

  reassignRoute(pickup: OperationsPickup) {
    pickup.isRouteAssigned = false;
  }

  changedRouteOptions(selectedRouteId: string, pickup: OperationsPickup) {
    const pickupBody: OnDemandPickupBody = {
      orderNumber: pickup.shipperPrn,
      route: selectedRouteId,
    };
    const valIndex = this.changedRoutes.findIndex(
      (val) => val.orderNumber === pickup.shipperPrn,
    );
    const val = this.changedRoutes[valIndex];

    if (valIndex > -1) {
      if (selectedRouteId !== undefined) {
        val.route = selectedRouteId;
      } else {
        this.changedRoutes.splice(valIndex, 1);
      }
    } else {
      this.changedRoutes.push(pickupBody);
    }
  }

  assignReassignRoute() {
    const request: AssignReassignRequest = {
      ondemandPickup: this.changedRoutes,
    };
    this.pickupAssignmentsService
      .assignReassign(request, this.locationCode)
      .pipe(
        take(1),
        filter((data) => data.code === 200),
      )
      .subscribe(() => {
        this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
        this.changedRoutes = [];
        this.refetchData.emit(true);
      });
  }
}
